import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Globe } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import type { AnalyticsSummary } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Card, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { RangePicker, useReportRange, type RangeSearch } from '@/features/reports';
import { useStore } from '@/shared/store/store-context';

import { useEventColumns } from '../components/event-columns';
import { eventFilters } from '../components/event-filters';
import { useAnalyticsEvents } from '../hooks/use-analytics-events';
import { useAnalyticsSummary } from '../hooks/use-analytics-summary';
import { toEventParams } from '../schemas/event-list-search';

const ROUTE_ID = '/_authed/_active/s/$storeId/reports/analytics' as const;
const ROUTE_PATH = '/s/$storeId/reports/analytics' as const;

function Stat({
  label,
  value,
  detail,
  isLoading,
}: {
  label: string;
  value: string;
  detail: string;
  isLoading: boolean;
}) {
  return (
    <div role="group" aria-label={label} className="contents">
      <StatCard label={label} value={value} detail={detail} isLoading={isLoading} />
    </div>
  );
}

function RowsSkeleton({ columns }: { columns: number }) {
  return Array.from({ length: 3 }).map((_, i) => (
    <TableRow key={i}>
      <TableCell colSpan={columns} className="px-5">
        <Skeleton className="h-5" />
      </TableCell>
    </TableRow>
  ));
}

function SummaryTables({
  summary,
  isLoading,
}: {
  summary: AnalyticsSummary | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation('analytics');
  const format = useFormat();
  const total = summary?.counts.reduce((sum, row) => sum + row.count, 0) ?? 0;

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card className="gap-0 overflow-hidden py-0" data-tour="reports.analytics.counts">
        <CardHeader className="py-4">
          <CardTitle className="text-[15px]">{t('report.counts')}</CardTitle>
        </CardHeader>
        <Table aria-label={t('report.counts')}>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">{t('columns.event')}</TableHead>
              <TableHead className="text-right">{t('columns.count')}</TableHead>
              <TableHead className="w-40 pr-5">{t('report.share')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <RowsSkeleton columns={3} />
            ) : summary && summary.counts.length > 0 ? (
              summary.counts.map((row) => {
                const share = total > 0 ? row.count / total : 0;
                return (
                  <TableRow key={row.name}>
                    <TableCell className="pl-5">
                      <span className="font-medium">{t(`eventNames.${row.name}`)}</span>{' '}
                      <code className="text-muted-foreground font-mono text-xs">{row.name}</code>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.number(row.count)}
                    </TableCell>
                    <TableCell className="pr-5">
                      <span className="flex items-center gap-2">
                        <span className="bg-muted h-1.5 flex-1 overflow-hidden rounded-full">
                          <span
                            className="bg-primary block h-full rounded-full"
                            style={{ width: `${share * 100}%` }}
                          />
                        </span>
                        <span className="text-muted-foreground w-12 text-right text-xs tabular-nums">
                          {format.percent(share)}
                        </span>
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground h-24 text-center">
                  {t('report.countsEmpty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Card
        className="gap-0 self-start overflow-hidden py-0"
        data-tour="reports.analytics.forwarding"
      >
        <CardHeader className="py-4">
          <CardTitle className="text-[15px]">{t('report.forwarding')}</CardTitle>
        </CardHeader>
        <Table aria-label={t('report.forwarding')}>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">{t('columns.provider')}</TableHead>
              <TableHead className="text-right">{t('columns.sent')}</TableHead>
              <TableHead className="text-right">{t('columns.failed')}</TableHead>
              <TableHead className="pr-5 text-right">{t('columns.skipped')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <RowsSkeleton columns={4} />
            ) : summary && summary.forwarding.length > 0 ? (
              summary.forwarding.map((row) => (
                <TableRow key={row.provider}>
                  <TableCell className="pl-5 font-medium">
                    {t(`providers.${row.provider}`)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {format.number(row.sent)}
                  </TableCell>
                  <TableCell
                    className={
                      row.failed > 0
                        ? 'text-destructive text-right tabular-nums'
                        : 'text-right tabular-nums'
                    }
                  >
                    {format.number(row.failed)}
                  </TableCell>
                  <TableCell className="pr-5 text-right tabular-nums">
                    {format.number(row.skipped)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground h-24 text-center">
                  {t('report.forwardingEmpty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function EventLog({ storeId, range }: { storeId: string; range: { from: string; to: string } }) {
  const { t } = useTranslation('analytics');
  const queryClient = useQueryClient();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const columns = useEventColumns();

  const filters = useMemo(
    () => eventFilters({ t, queryClient, storeId }),
    [queryClient, storeId, t],
  );
  // The log sits below the summary: a filter or page change must not scroll the window to the top.
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
        resetScroll: false,
      }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useAnalyticsEvents(storeId, toEventParams(list.params, range));
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'analytics-events',
  });

  return (
    <section
      aria-labelledby="analytics-log"
      className="flex flex-col gap-3"
      data-tour="reports.analytics.log"
    >
      <div>
        <h2 id="analytics-log" className="text-[15px] font-semibold">
          {t('report.log')}
        </h2>
        <p className="text-muted-foreground text-sm">{t('report.logDescription')}</p>
      </div>
      <DataTable
        table={table}
        search={false}
        loading={query.isPending}
        fetching={query.isFetching}
        error={query.error}
        onRetry={() => void query.refetch()}
        getRowLabel={(row) => `${row.name} ${row.occurredAt}`}
        resourceName={{ singular: t('report.resource.one'), plural: t('report.resource.other') }}
        emptyState={{ title: t('report.logEmpty') }}
      />
    </section>
  );
}

function AnalyticsReport() {
  const { t } = useTranslation('analytics');
  const store = useStore();
  const format = useFormat();
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { range } = useReportRange();
  const query = useAnalyticsSummary(store.id, range);
  const summary = query.data;
  const events = summary?.counts.reduce((sum, row) => sum + row.count, 0);
  const purchases = summary?.counts.find((row) => row.name === 'purchase')?.count ?? 0;

  // A new range starts the log from its first page.
  const setRange = (next: RangeSearch) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        range: next.range,
        from: next.from,
        to: next.to,
        cursor: undefined,
      }),
    });

  return (
    <div className="flex flex-col gap-4">
      <RangePicker range={range} onChange={setRange} />
      <Alert className="-mt-2">
        <Globe aria-hidden />
        <AlertDescription>{t('report.utcNote')}</AlertDescription>
      </Alert>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3" data-tour="reports.analytics.kpis">
            <Stat
              label={t('report.sessions')}
              value={summary ? format.number(summary.sessions) : '—'}
              detail={t('report.sessionsDetail')}
              isLoading={query.isPending}
            />
            <Stat
              label={t('report.events')}
              value={events !== undefined ? format.number(events) : '—'}
              detail={t('report.eventsDetail', { count: summary?.counts.length ?? 0 })}
              isLoading={query.isPending}
            />
            <Stat
              label={t('report.purchases')}
              value={summary ? format.number(purchases) : '—'}
              detail={t('report.purchasesDetail')}
              isLoading={query.isPending}
            />
          </div>
          <SummaryTables summary={summary} isLoading={query.isPending} />
        </>
      )}
      <EventLog storeId={store.id} range={range} />
    </div>
  );
}

export function AnalyticsReportPage() {
  return (
    <FeatureErrorBoundary name="reports.analytics">
      <AnalyticsReport />
    </FeatureErrorBoundary>
  );
}
